
import Vue from 'vue';
import { bridge, api, env, analytics } from '@/common';
import { isSupportFaceID } from '@/common/utils';
import { Toggle as VixToggle, toast } from '@mx/vix';
import { analyticConfig } from '@/config/analyticConfig';
import { monitor } from '@/common/monitor';
import modal from '@/service/modal';
export default Vue.extend({
  name: 'TouchPayCell',
  props: {
    config: Object as () => ITouchPay
  },
  components: {
    VixToggle
  },
  computed: {
    isOpen(): boolean {
      if (this.config.isOpen) {
        if (
          env.biometricsInfoParams.has_touchid !== 0 ||
          (env.biometricsInfoParams.has_faceid !== 0 &&
            isSupportFaceID(env) &&
            env.isIOS)
        ) {
          return true;
        }
      }
      return false;
    },
    descTip(): string {
      return this.isOpen ? '去关闭': '去开启'
    }
  },
  methods: {
    openLink(url: string) {
      bridge.openPage(url);
    },

    beforeTouchPayChange() {
      analytics.click(analyticConfig.paySetting.bid.clickItem, {
        item: '生物设置',
        scene: env.scene,
        from: 'pay-setting'
      });
      if (this.isOpen) {
        // 关闭流程
        this.handleDisableTouchPay();
      } else {
        // 开启流程
        this.handleEnableTouchPay();
      }
      return false;
    },

    /**
     * 开启状态下关闭流程：
     * 1.弹出确认关闭弹窗
     * 2.请求是否允许关闭开启指纹的接口 (/api/wallet/touch-pay-allowed)
     * 3.调用后台接口关闭指纹/面容支付（/api/wallet/disable-touch-pay）
     * 4.更新 Native 本地关闭状态
     */
    async handleDisableTouchPay() {
      await this.confirmDisableModal();
      const { isAllowed, rejectDesc } = await this.queryTouchPayAllowed(
        'disable'
      );
      if (isAllowed) {
        const {
          success: isSuccess,
          challenge: challenge,
          message
        } = await api.disableTouchPay({
          printType: env.biometricsInfoParams.printType
        });
        if (isSuccess) {
          const params = {
            open_or_not: false,
            challenge: challenge || '',
            finger_type: this.config.finger_type
          };
          // 安卓需要调桥，ios不需要
          if (env.isAndroid) {
            bridge.changeFingerPay(
              params,
              (response): any => {
                if (message) {
                  this.$emit('success', message);
                } else {
                  // 有时不返回message
                  this.$emit('success', '已关闭');
                }
              },
              (error): any => {
                toast.show('error', '操作失败');
                // cat上报失败数据
                monitor.captureMessage({
                  name: '安卓关闭touch-pay调用桥失败',
                  msg: JSON.stringify({
                    href: location.href,
                    userAgent: navigator.userAgent,
                    queryString: env.commonParams
                  })
                });
              }
            );
          } else {
            this.$emit('success', message);
          }
        } else {
          toast.show('error', message);
          // cat上报失败数据
          monitor.captureMessage({
            name: '安卓关闭touch-pay调用disable-touch-pay接口返回失败',
            msg: JSON.stringify({
              href: location.href,
              userAgent: navigator.userAgent,
              queryString: env.commonParams
            })
          });
        }
      } else {
        this.showIOSGuidesettingsModal(rejectDesc);
      }
      return false;
    },

    /**
     * 关闭状态下开启流程：
     * 1.是否有已录入的指纹/面容(通过 has_touchid/has_faceid 判断；是：去开启流程，否：去设置流程)
     * 2.请求是否允许开启开启指纹的接口 (/api/wallet/touch-pay-allowed)
     * 3.通过密码验证页面获取 paypass
     * 4.调用后台接口开启指纹/面容支付（/api/wallet/enable-touch-pay）
     * 5.更新 Native 本地开启状态
     */
    async handleEnableTouchPay() {
      await env.initEnv();
      if (env.isIOS) {
        if (env.biometricsInfoParams.has_faceid === 0 && isSupportFaceID(env)) {
          // 支持面容但是没有设置面容
          return this.showIOSGuidesettingsModal(
            '请在系统[设置]-[面容 ID 与密码]中开启面容 ID 功能，以便使用面容 ID 支付。'
          );
        } else if (
          env.biometricsInfoParams.has_faceid === 0 &&
          !isSupportFaceID(env) &&
          env.biometricsInfoParams.has_touchid === 0
        ) {
          // 不支持面容也没有设置面容和指纹
          return this.showIOSGuidesettingsModal(
            '请在系统[设置]-[Touch ID与密码]中开启Touch ID功能，以便使用指纹支付。'
          );
        }
      }
      if (env.isAndroid) {
        if (env.biometricsInfoParams.has_touchid === 0) {
          return this.showAndroidGuidesettingsModal();
        }
      }
      const {
        isAllowed,
        pageTip,
        rejectDesc
      } = await this.queryTouchPayAllowed('enable');
      if (isAllowed) {
        this.$emit('openTouchPay', pageTip, this.config.finger_type);
      } else {
        this.showIOSGuidesettingsModal(rejectDesc);
      }
    },

    /**
     * 确认取消弹框
     */
    confirmDisableModal() {
      return new Promise<void>((resolve, reject) => {
        this.$toast.hide();
        this.$emit('modalShow');
        modal.show({
          title: `确定暂停${this.config.title.indexOf('指纹') !== -1 ? '指纹' : '面容'}支付吗`,
          content: this.config.disableDesc,
          preventScroll: true,
          button: [
            {
              text: this.config.cancelButtonDesc,
              onClick: () => {
                modal.hide();
                this.$emit('modalHide');
              }
            },
            {
              text: this.config.disableButtonDesc,
              onClick: () => {
                resolve();
                modal.hide();
                this.$emit('modalHide');
              }
            }
          ]
        });
      });
    },

    /**
     * operType: 操作类型，enable、disable
     */
    async queryTouchPayAllowed(operType: string) {
      return await api.touchPayAllowed({
        operType,
        printType: env.biometricsInfoParams.printType
      });
    },

    /**
     * 展示没有权限的弹框
     */
    showIOSGuidesettingsModal(message: string) {
      toast.hide();
      this.$emit('modalShow');
      modal.show({
        title: '提示',
        content: message,
        preventScroll: true,
        button: {
          text: '知道了',
          onClick: () => {
            modal.hide();
            this.$emit('modalHide');
          }
        }
      });
    },

    showAndroidGuidesettingsModal() {
      toast.hide();
      this.$emit('modalShow');
      modal.show({
        title: '提示',
        content: '请在系统设置中开启指纹功能，以使用指纹支付',
        preventScroll: true,
        button: [
          {
            text: '取消',
            onClick: () => {
              modal.hide();
              this.$emit('modalHide');
            }
          },
          {
            text: '去设置',
            onClick: () => {
              bridge.setFingerPay(
                (response): any => {
                  // 调起成功
                },
                (error): any => {
                  toast.show('text', error);
                }
              );
              modal.hide();
              this.$emit('modalHide');
            }
          }
        ]
      });
    }
  }
});
